import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {Box} from '@mui/material';
import {v4 as uuidv4} from 'uuid';
import './ChipPanel.scss';
import Chip from './chip/Chip';
import {Criteria} from '../../../../types/Criteria';
import {useAutocomplete} from '../../../../../../hooks/session-search/useSessionSearch';
import ArrAutocomplete from '../../../../../common/arr-autocomplete/ArrAutocomplete';
import {formatAutocompleteResults} from '../../autocompleteUtils';

export interface ChipPanelProps {
  criteria: Criteria[];
  onCriteriaChange?: (updatedCriteria: Criteria[]) => void;
  dataTestId: string;
  resolveSavedSearchDisplayValue?: (value: string) => string | undefined;
}

export interface ChipPanelHandle {
  insertCriteria: (criteria: Criteria) => void;
}

/**
 * The chip row works like a line of text. A "gap" is a spot where you can
 * type - that's where the input box shows up. A "chip" is a spot where an
 * existing tag is highlighted instead. Pressing an arrow key always moves
 * one spot at a time, switching back and forth between a gap and a chip -
 * the same way a text cursor moves between letters one at a time.
 */
const CursorType = {
  Gap: 'gap',
  Chip: 'chip',
} as const;
type CursorKind = (typeof CursorType)[keyof typeof CursorType];
type Cursor = {type: CursorKind; index: number} | null;

// What kind of element a queued .focus() call should target once it mounts.
const FocusTargetType = {
  Input: 'input',
  Chip: 'chip',
} as const;
type PendingFocus =
  | {type: typeof FocusTargetType.Input}
  | {type: typeof FocusTargetType.Chip; index: number}
  | null;

const ChipPanel = forwardRef<ChipPanelHandle, ChipPanelProps>(
  (
    {criteria, onCriteriaChange, dataTestId, resolveSavedSearchDisplayValue},
    ref
  ) => {
    const [chips, setChips] = useState(criteria);
    const [cursor, setCursor] = useState<Cursor>(null);
    const [inputValue, setInputValue] = useState('');

    const [autocompleteEnabled, setAutocompleteEnabled] =
      useState<boolean>(false);
    const [autocompleteItems, setAutocompleteItems] = useState<string[]>([]);
    const {data, refetch} = useAutocomplete(inputValue);
    const lastFocusedChipRef = useRef<HTMLElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const chipRefs = useRef<(HTMLElement | null)[]>([]);
    const pendingFocusRef = useRef<PendingFocus>(null);
    /**
     * Remembers where the cursor was, even after the input box closes.
     * This matters because buttons like Metadata, Comments, and Operator
     * live outside this component, so clicking them closes the input box
     * first. Without this, the app would forget where the user was
     * working and add the new item at the end instead of the right spot.
     */
    const lastPositionRef = useRef<number | null>(null);
    /**
     * Live copy of inputValue so Tab can clear it synchronously.
     * handleBlur still runs after Tab (focus leaving / unmount) and
     * would otherwise insert a second chip from the stale state value.
     */
    const inputValueRef = useRef(inputValue);
    inputValueRef.current = inputValue;
    /**
     * After Tab commits a chip, keep focus off that chip. Native Tab
     * would land on it (tabIndex=0) and :focus-within shows the clear
     * icon; Enter never does that because focus stays in the next gap.
     */
    const focusSearchAfterTabRef = useRef(false);
    /**
     * Clicking the empty row blurs the input (relatedTarget is this
     * panel) then fires click on the panel. Closing the empty gap on
     * blur would otherwise be undone by handlePanelClick reopening it.
     */
    const ignoreNextPanelClickRef = useRef(false);

    const resolveSavedSearchCriteria = useCallback(
      (criterion: Criteria): Criteria => {
        if (
          criterion.type !== 'Operand' ||
          typeof criterion.value !== 'string' ||
          !resolveSavedSearchDisplayValue
        ) {
          return criterion;
        }

        if (criterion.displayValue) {
          return criterion;
        }

        const displayValue = resolveSavedSearchDisplayValue(criterion.value);
        if (!displayValue || displayValue === criterion.displayValue) {
          return criterion;
        }

        return {...criterion, displayValue};
      },
      [resolveSavedSearchDisplayValue]
    );

    useEffect(() => {
      const resolvedCriteria = criteria.map(resolveSavedSearchCriteria);
      setChips(resolvedCriteria);
      setAutocompleteItems([]);
      if (
        resolvedCriteria.some(
          (criterion, index) => criterion !== criteria[index]
        )
      ) {
        onCriteriaChange?.(resolvedCriteria);
      }
    }, [criteria, onCriteriaChange, resolveSavedSearchCriteria]);

    /**
     * When we decide the input box or a chip should be focused, the
     * browser needs a moment to actually draw that element on screen
     * before we can place the cursor in it. This runs after every screen
     * update and finishes that job as soon as the element is ready.
     */
    useEffect(() => {
      const pending = pendingFocusRef.current;
      if (!pending) {
        return;
      }
      pendingFocusRef.current = null;
      if (pending.type === FocusTargetType.Input) {
        inputRef.current?.focus();
      } else {
        chipRefs.current[pending.index]?.focus();
      }
    });

    useEffect(() => {
      if (!focusSearchAfterTabRef.current) {
        return;
      }
      focusSearchAfterTabRef.current = false;
      const searchButton = document.querySelector<HTMLElement>(
        '[data-testid="button-search"]'
      );
      if (searchButton) {
        searchButton.focus();
        return;
      }
      panelRef.current?.focus();
    });

    useEffect(() => {
      refetch();
    }, [inputValue]);

    useEffect(() => {
      const suggestions = formatAutocompleteResults(data ?? []);
      setAutocompleteItems(suggestions);
    }, [data]);

    // Where a new item lands: a gap is that spot already, a highlighted chip
    // means the spot after it - so an operator follows the term just inserted.
    const slotFor = (position: {type: CursorKind; index: number}) =>
      position.type === CursorType.Chip ? position.index + 1 : position.index;

    /**
     * Works out exactly where a new value or a button click (Operator,
     * Metadata, Comments) should be added: at the current cursor spot,
     * or - if the input box has already closed - at the last place the
     * user was working (see lastPositionRef). If the user never clicked
     * into this row at all, it defaults to the end.
     */
    const insertionIndex = () =>
      cursor !== null
        ? slotFor(cursor)
        : (lastPositionRef.current ?? chips.length);

    const insertAt = (index: number, newChip: Criteria) => {
      const resolvedChip = resolveSavedSearchCriteria(newChip);
      const updated = [...chips];
      updated.splice(index, 0, resolvedChip);
      setChips(updated);
      onCriteriaChange && onCriteriaChange(updated);
    };

    const createOperandCriteria = (value: string): Criteria => ({
      id: uuidv4(),
      type: 'Operand',
      value,
    });

    const deleteCriteria = (target: Criteria) => {
      ignoreNextPanelClickRef.current = false;
      const index = chips.findIndex((chip) => chip.id === target.id);
      const updated = chips.filter((chip) => chip.id !== target.id);
      setChips(updated);
      onCriteriaChange?.(updated);

      setCursor((prev) => {
        const next =
          prev && index !== -1 && index < prev.index
            ? {...prev, index: prev.index - 1}
            : prev;
        if (next) {
          lastPositionRef.current = slotFor(next);
        }
        return next;
      });
    };

    const handleEdit = (updatedCriteria: Criteria) => {
      const currentCriteria = chips.find(
        (criterion) => criterion.id === updatedCriteria.id
      );
      const criteriaToResolve = {...updatedCriteria};
      if (currentCriteria && currentCriteria.value !== updatedCriteria.value) {
        delete criteriaToResolve.displayValue;
      }
      const resolvedCriteria = resolveSavedSearchCriteria(criteriaToResolve);
      setChips((prev) => {
        const updated = prev.map((c) =>
          c.id === resolvedCriteria.id ? resolvedCriteria : c
        );

        onCriteriaChange?.(updated);

        return updated;
      });
    };

    /**
     * Moves the cursor to a gap or a chip, remembers the spot (see
     * lastPositionRef), and queues up focusing the right element once
     * it's on screen. Used any time the cursor moves, whether that's
     * opening the input box, landing on a chip, or highlighting a chip
     * that was just added.
     */
    const moveCursorTo = (
      next: {type: CursorKind; index: number},
      options?: {focus?: boolean}
    ) => {
      setCursor(next);
      lastPositionRef.current = slotFor(next);
      if (options?.focus === false) {
        pendingFocusRef.current = null;
        return;
      }
      pendingFocusRef.current =
        next.type === CursorType.Gap
          ? {type: FocusTargetType.Input}
          : {type: FocusTargetType.Chip, index: next.index};
    };

    const openGapAt = (index: number, options?: {focus?: boolean}) =>
      moveCursorTo({type: CursorType.Gap, index}, options);

    const focusChipAt = (index: number) =>
      moveCursorTo({type: CursorType.Chip, index});

    /**
     * Clicking anywhere in the empty part of the row reopens the input
     * box at the end, ready to type - unless it's already open, in which
     * case we leave it exactly where it is. Without this, clicking the
     * row right after adding something via the Operator button would do
     * nothing, because a chip (not the input box) was still highlighted.
     */
    const handlePanelClick = () => {
      if (ignoreNextPanelClickRef.current) {
        ignoreNextPanelClickRef.current = false;
        return;
      }
      if (cursor?.type !== CursorType.Gap) {
        openGapAt(chips.length);
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    };

    const addCriteria = (options?: {focusNext?: boolean}) => {
      const value = inputValueRef.current;
      if (!value) {
        return;
      }
      const index = insertionIndex();
      insertAt(index, createOperandCriteria(value));
      inputValueRef.current = '';
      setInputValue('');
      openGapAt(index + 1, {focus: options?.focusNext !== false});
    };

    /**
     * Saves typed text as a chip and closes the gap. Used when the
     * user leaves the field (Tab, Shift+Tab, or blur outside the row).
     * Enter uses addCriteria instead so the next gap stays open.
     */
    const commitPendingAndClose = () => {
      const value = inputValueRef.current;
      if (value) {
        const index = insertionIndex();
        insertAt(index, createOperandCriteria(value));
        inputValueRef.current = '';
        setInputValue('');
        lastPositionRef.current = index + 1;
      }
      setCursor(null);
    };

    const clearCriteria = () => {
      inputValueRef.current = '';
      setCursor(null);
      setInputValue('');
      lastPositionRef.current = null;
    };

    const isCaretAt = (input: HTMLInputElement, position: number) =>
      input.selectionStart === position && input.selectionEnd === position;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (cursor?.type !== CursorType.Gap) {
        return;
      }
      const gapIndex = cursor.index;
      const input = e.currentTarget;

      if (e.key === 'Enter') {
        if (inputValue) {
          addCriteria();
        } else {
          clearCriteria();
        }
      } else if (e.key === 'Escape') {
        clearCriteria();
      } else if (e.key === 'Tab') {
        if (inputValue) {
          /**
           * Tab / Shift+Tab commit the pending string and close the
           * input so Search and New Search can enable. Forward Tab
           * must not land on the new chip (that shows the hover-only
           * clear icon via :focus-within). Shift+Tab keeps native order.
           */
          commitPendingAndClose();
          if (!e.shiftKey) {
            e.preventDefault();
            focusSearchAfterTabRef.current = true;
          }
        } else {
          commitPendingAndClose();
        }
      } else if (e.key === 'ArrowLeft' && isCaretAt(input, 0) && gapIndex > 0) {
        e.preventDefault();
        focusChipAt(gapIndex - 1);
      } else if (
        e.key === 'ArrowRight' &&
        isCaretAt(input, input.value.length) &&
        gapIndex < chips.length
      ) {
        e.preventDefault();
        focusChipAt(gapIndex);
      }
    };

    /**
     * Grows or shrinks the input box to fit what's typed in it. This has
     * to be done here in code instead of in CSS, because the CSS trick
     * for this ("field-sizing: content") doesn't work in Safari or
     * Firefox yet.
     */
    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
      const paddingLeft = 8;
      const paddingRight = 8;
      const defaultWidth = 50 - paddingLeft - paddingRight;
      e.currentTarget.style.width = `${defaultWidth}px`;
      e.currentTarget.style.width = `${e.currentTarget.scrollWidth}px`;

      setInputValue(e.currentTarget.value);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const relatedTarget = e.relatedTarget as Node | null;
      const relatedElement =
        relatedTarget instanceof Element ? relatedTarget : null;
      const focusStaysInPanel = Boolean(
        relatedElement && panelRef.current?.contains(relatedElement)
      );
      const focusMovesToChip = Boolean(
        relatedElement?.closest('[data-chip-index]')
      );
      /**
       * Empty gap: close whenever focus leaves the input, including a
       * click on this row. Ignore the following panel click so the gap
       * is not immediately reopened. Clicks on a chip (edit or clear)
       * are not panel clicks, so they must not consume the next one.
       */
      if (!inputValueRef.current) {
        if (focusStaysInPanel && !focusMovesToChip) {
          ignoreNextPanelClickRef.current = true;
        }
        commitPendingAndClose();
        return;
      }
      /**
       * Arrow-key focus onto a sibling chip is not leaving the row.
       * Keep the in-progress text.
       */
      if (focusMovesToChip) {
        return;
      }

      /**
       * Click on the empty part of this row: turn typed text into a
       * chip and open a new empty input. Clicking Search or any other
       * control outside the panel only commits the chip.
       */
      if (focusStaysInPanel) {
        addCriteria({focusNext: true});
        return;
      }
      commitPendingAndClose();
    };

    const handleRowKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const indexAttr = target.getAttribute?.('data-chip-index');
      if (indexAttr === null || indexAttr === undefined) {
        return;
      }
      const index = Number(indexAttr);

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        openGapAt(index);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        openGapAt(index + 1);
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        const chipToDelete = chips[index];
        if (chipToDelete) {
          deleteCriteria(chipToDelete);
          openGapAt(index);
        }
      }
    };

    useImperativeHandle(ref, () => ({
      insertCriteria: (newCriteria: Criteria) => {
        const index = insertionIndex();
        insertAt(index, newCriteria);
        /**
         * Highlight the chip that was just added, instead of leaving an
         * empty input box after it - this makes it obvious what was just
         * added, and keeps the next item going in right after it.
         */
        focusChipAt(index);
      },
    }));

    const gapIndex = cursor?.type === CursorType.Gap ? cursor.index : null;

    const renderChip = (c: Criteria, index: number) => (
      <Chip
        key={c.id}
        criteria={c}
        index={index}
        onEdit={handleEdit}
        onDelete={deleteCriteria}
        dataTestId={`chip-panel-${dataTestId}-${index}`}
        chipFocusRef={lastFocusedChipRef}
        registerRef={(el) => {
          chipRefs.current[index] = el;
        }}
      />
    );

    const inputNode = gapIndex !== null && (
      <ArrAutocomplete
        key="chip-panel-input"
        className="chip-autocomplete"
        value={inputValue}
        options={autocompleteItems}
        onChange={setInputValue}
        enabled={autocompleteEnabled}
        data-testid={`chip-panel-autocomplete--${dataTestId}`}
      >
        <input
          type="text"
          className="chip-input"
          aria-label="Add chip"
          ref={inputRef}
          value={inputValue}
          onFocus={() => setAutocompleteEnabled(true)}
          onBlur={(e) => {
            handleBlur(e);
            setAutocompleteEnabled(false);
          }}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onInput={handleInput}
          data-testid={`chip-panel-input--${dataTestId}`}
        />
      </ArrAutocomplete>
    );

    /**
     * All the chips and the input box are built into one single list, in
     * one pass, instead of two separate lists split around the input box.
     * Reason: when a chip moves from one side of the input box to the
     * other, React needs to see it as the "same" chip moving, not a new
     * chip appearing - otherwise it disappears and reappears, and if it
     * had the keyboard focus, that focus gets lost.
     */
    const rowChildren: React.ReactNode[] = [];
    chips.forEach((c, index) => {
      if (gapIndex === index && inputNode) {
        rowChildren.push(inputNode);
      }
      rowChildren.push(renderChip(c, index));
    });
    if (gapIndex === chips.length && inputNode) {
      rowChildren.push(inputNode);
    }

    return (
      <Box
        className="chip-panel"
        onClick={handlePanelClick}
        role="group"
        aria-label="Search chip panel"
        data-testid={`chip-panle-clickable-${dataTestId}`}
        tabIndex={0}
        ref={panelRef}
      >
        <Box
          className="chip-row"
          data-testid={`chip-row-${dataTestId}`}
          onKeyDown={handleRowKeyDown}
        >
          {rowChildren}
        </Box>
      </Box>
    );
  }
);

export default ChipPanel;
